// src/components/shared/modals/FeedbackModal.tsx
// Modal for collecting user feedback.
import { useState, useCallback, useEffect, type KeyboardEvent } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { typedApi } from '../../../services/typedApiClient';
import type { components } from '../../../types/api.generated';
import { useFeedbackContext } from '../../../hooks/useFeedbackContext';
import BaseModal from './BaseModal';
import ModalFooter from './ModalFooter';

/**
 * Derived from the generated client, not hand-written.
 *
 * Restating the union here would make this a second declaration site for a
 * vocabulary the backend owns — the exact shape of the bug fixed in v8.49.1,
 * where `adminService` hand-declared the feedback STATUS set, drifted from the
 * Pydantic Literal, and put three admin buttons permanently on a 400. Deriving
 * it means a backend change to the Literal either regenerates into this type or
 * fails the build.
 */
export type FeedbackCategory = NonNullable<
  NonNullable<components['schemas']['SubmitFeedbackRequest']['category']>
>;

// Deliberately NOT by-surface (Audit / Planner / Chat): the attached context
// already carries `route`, which names the surface with no click. The one
// optional field is spent on the axis nothing else gives us — is it broken —
// because that is what decides whether this gets looked at today or Friday.
const CATEGORIES = [
  { value: 'broken', label: "Something's broken" },
  { value: 'confusing', label: 'Confusing' },
  { value: 'suggestion', label: 'Suggestion' },
] as const satisfies ReadonlyArray<{ value: FeedbackCategory; label: string }>;

/** Matches Pydantic max_length, feedback_service.MAX_FEEDBACK_LENGTH and the DB CHECK. */
const MAX_LENGTH = 2000;
const WARN_AT = Math.floor(MAX_LENGTH * 0.9);

export interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Active plan id, attached to the report. Optional — absence never blocks submission. */
  planId?: number | null;
}

const FeedbackModal = ({ isOpen, onClose, planId }: FeedbackModalProps) => {
  const { user } = useAuth();
  const buildContext = useFeedbackContext({ planId });
  const [feedback, setFeedback] = useState('');
  const [category, setCategory] = useState<FeedbackCategory | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setFeedback('');
      setCategory(null);
      setSubmitted(false);
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!feedback.trim() || feedback.length > MAX_LENGTH || isSubmitting) {
      return;
    }

    if (!user || !user.id) {
      setError('Please log in to submit feedback');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Assembled at submit time, and never allowed to fail the submission.
      let context;
      try {
        context = await buildContext();
      } catch {
        context = undefined;
      }

      await typedApi.feedback.submit({
        feedback_text: feedback.trim(),
        ...(category ? { category } : {}),
        ...(context && Object.keys(context).length > 0 ? { context } : {}),
      });

      setSubmitted(true);
      setFeedback('');

      setTimeout(() => {
        onClose();
        setSubmitted(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      setError((err as { message?: string })?.message || 'Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [feedback, category, isSubmitting, user, onClose, buildContext]);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Share Your Feedback"
      subtitle="Help us improve the academic planner"
    >
      <div className="p-6">
        {submitted ? (
          <div className="text-center py-8">
            <CheckCircle className="h-16 w-16 text-status-completed mx-auto mb-4" />
            <h3 className="text-lg font-medium text-ink-1 mb-2">
              Thank you for your feedback!
            </h3>
            <p className="text-sm text-ink-2">
              Your input helps us make the planner better for everyone.
            </p>
          </div>
        ) : !user ? (
          <div className="text-center py-8">
            <p className="text-ink-2">Please log in to submit feedback.</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 bg-status-critical/10 border border-status-critical/25 rounded-card">
                <p className="text-sm text-status-critical-fg">{error}</p>
              </div>
            )}

            <div className="mb-4">
              <span className="block text-sm font-medium text-ink-1 mb-2">
                What kind of feedback is this? <span className="text-ink-3 font-normal">(optional)</span>
              </span>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Feedback category">
                {CATEGORIES.map(({ value, label }) => {
                  const selected = category === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCategory(selected ? null : value)}
                      aria-pressed={selected}
                      disabled={isSubmitting}
                      className={`px-3 py-1.5 text-sm rounded-control border transition-colors ${
                        selected
                          ? 'bg-primary text-white border-primary'
                          : 'bg-surface-card text-ink-2 border-line-soft hover:bg-surface-sunken'
                      } disabled:opacity-50`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="feedback-text" className="block text-sm font-medium text-ink-1 mb-2">
                What would you like to share?
              </label>
              <textarea
                id="feedback-text"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Tell us about bugs, feature requests, or anything else. For a bug, what you did and what happened is the most useful thing you can write."
                className="w-full p-3 bg-surface-card text-ink-1 placeholder:text-ink-3 border border-line-soft rounded-control text-sm resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                rows={6}
                maxLength={MAX_LENGTH}
                disabled={isSubmitting}
                autoFocus
              />
              <div className="flex items-center justify-between mt-2">
                <span
                  className={`text-xs ${
                    feedback.length > WARN_AT ? 'text-status-warning-fg font-medium' : 'text-ink-3'
                  }`}
                >
                  {feedback.length}/{MAX_LENGTH} characters
                </span>
                <span className="text-xs text-ink-3">Ctrl+Enter to submit</span>
              </div>
            </div>

            {/* Told, not attached silently. This sentence is what makes the
                privacy policy's disclosure true rather than a fig leaf. */}
            <p className="text-xs text-ink-3">
              We'll also attach the page you're on, your current plan, which school is
              active, the app version, your browser and your screen size, so we can
              reproduce what you saw.
            </p>
          </>
        )}
      </div>

      {!submitted && user && (
        <ModalFooter
          onCancel={onClose}
          onSubmit={handleSubmit}
          submitText="Submit Feedback"
          loadingText="Sending..."
          variant="amber"
          isLoading={isSubmitting}
          disabled={!feedback.trim() || feedback.length > MAX_LENGTH}
          submitIcon={Send}
        />
      )}
    </BaseModal>
  );
};

export default FeedbackModal;
